import { dispatchQtyChange } from '@trade/events';

const RECENT_EDIT_MS = 300;

class TradeQty extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('blur', this.#onBlur, { signal, capture: true });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /**
   * @param {number} value
   */
  setValue(value) {
    const input = this.querySelector('input[data-cart-quantity]');
    if (input instanceof HTMLInputElement) {
      input.value = String(value);
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[name]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const input = this.querySelector('input[data-cart-quantity]');
    if (!(input instanceof HTMLInputElement)) return;

    const step = parseInt(input.step || '1', 10) || 1;
    const min = parseInt(input.min || '0', 10) || 0;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    let next = parseInt(input.value || '0', 10) || 0;

    if (button.name === 'plus') {
      next = Math.min(max, next + step);
    } else if (button.name === 'minus') {
      next = Math.max(min, next - step);
    } else {
      return;
    }

    input.value = String(next);
    this.#emitChange(next);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('input[data-cart-quantity]')) return;

    const quantity = this.#clampQuantity(event.target);
    event.target.value = String(quantity);
    this.#emitChange(quantity);
  };

  /** @param {FocusEvent} event */
  #onBlur = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('input[data-cart-quantity]')) return;

    const quantity = this.#clampQuantity(event.target);
    event.target.value = String(quantity);
    this.#emitChange(quantity);

    import('@trade/cart-api')
      .then(({ flushTradeCartNow }) => flushTradeCartNow())
      .catch(() => {});
  };

  /**
   * @param {HTMLInputElement} input
   */
  #clampQuantity(input) {
    const min = parseInt(input.min || '0', 10) || 0;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    let value = parseInt(input.value || '0', 10);
    if (isNaN(value)) value = min;
    return Math.max(min, Math.min(max, value));
  }

  /**
   * @param {number} quantity
   */
  #emitChange(quantity) {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.dataset.recentEditAt = String(Date.now());
    dispatchQtyChange(this, { variantId, quantity });
  }

  /**
   * @param {HTMLElement} qtyEl
   */
  static isRecentlyEdited(qtyEl) {
    if (!(qtyEl instanceof HTMLElement)) return false;
    if (qtyEl.contains(document.activeElement)) return true;

    const ts = parseInt(qtyEl.dataset.recentEditAt || '0', 10);
    return ts > 0 && Date.now() - ts < RECENT_EDIT_MS;
  }
}

if (!customElements.get('trade-qty')) {
  customElements.define('trade-qty', TradeQty);
}

export { TradeQty };
